/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import "./QRLoader.css";
import EmailSignatureService from "../../services/EmailSignatureService";
import { Loader } from "../Loader/Loader";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { QR_EXPIRATION_TIME } from "../../constants/GeneralConstants";
import { useNavigate } from "react-router-dom";
import { useWebSocket, parseWsPayload } from "../../hooks/useWebSocket";

const WS_HUB_ID = process.env.REACT_APP_WS_HUB_ID;

export const QRLoader = ({ signerGuid, setReceivedGraphData, additionalSignature }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loadingQRImage, setLoadingQR] = useState(false);
  const [qrData, setQRData] = useState({});
  const [showQR, setShowQR] = useState(true);
  const { qr64Data, qrToken } = qrData;
  const qrExpirationRef = useRef(null);
  const processedSignaturesRef = useRef(new Set());

  const wsGroupId = showQR && qrToken ? qrToken : null;

  const handleWsMessage = (data) => {
    if (data?.Type !== 3) return;

    const payload = parseWsPayload(data.Message);
    if (!payload) return;

    // Ignoramos nuestros propios ACK si el hub nos los devuelve.
    if (payload.Ack) return;

    const { SignatureImage, SignatureRawData, SignatureId } = payload;
    if (!SignatureImage) return;

    console.log('WebSocket QR message received');

    if (SignatureId) {
      // Confirmamos siempre, incluso los duplicados: si el móvil está
      // reintentando es porque no le llegó el ACK anterior.
      sendMessage({ Ack: SignatureId });
      if (processedSignaturesRef.current.has(SignatureId)) return;
      processedSignaturesRef.current.add(SignatureId);
    }

    setReceivedGraphData({ SignatureImage, SignatureRawData });
  };

  const { disconnect, sendMessage, connectionState } = useWebSocket(
    WS_HUB_ID,
    wsGroupId,
    1,
    handleWsMessage
  );

  const isConnected = connectionState === WebSocket.OPEN;

  useEffect(() => {
    getQRData();
    return () => {
      clearTimeout(qrExpirationRef.current);
      disconnect();
    };
  }, []);

  useEffect(() => {
    if (showQR && qrToken) {
      startTimer();
    }
  }, [showQR, qrToken]);

  const getQRData = () => {
    setLoadingQR(true);
    const body = { SignersGuid: [signerGuid] };

    EmailSignatureService.getQRData(body)
      .then((response) => {
        const { QRCodeImage, Token } = response.data;
        setQRData({ qr64Data: QRCodeImage, qrToken: Token });
        setShowQR(true);
      })
      .catch(e => {
        const error = e.response?.data;
        if (error?.toLowerCase().includes('invalid authentication')) {
          navigate('/expired-personal-code');
        }
      })
      .finally(() => setLoadingQR(false));
  };

  const expireQR = () => {
    disconnect();
    setShowQR(false);
  };

  const startTimer = () => {
    // Sin limpiar el timer anterior se acumulaban temporizadores huérfanos que
    // al vencer cerraban la conexión vigente y invalidaban un QR recién creado.
    clearTimeout(qrExpirationRef.current);
    qrExpirationRef.current = setTimeout(expireQR, QR_EXPIRATION_TIME);
  };

  const resetQR = () => {
    clearTimeout(qrExpirationRef.current);
    disconnect();
    processedSignaturesRef.current.clear();
    setShowQR(true);
    setQRData({});
    getQRData();
  };

  return (
    <>
      {loadingQRImage ? (
        <Loader />
      ) : (
        <Container className={additionalSignature ? "qr-container-reverse" : "qr-container"}>
          {showQR ? (
            <>
              <img
                src={`data:image/png;base64,${qr64Data}`}
                className="qr-image"
                alt="QR Code"
              />
              <p className="qr-instructions-title">{t("sign.better_experience")}</p>
              <p>{t("sign.you_can_scan")}</p>
              {qrToken && !isConnected && (
                <p className="qr-reconnecting">{t("sign.qr.reconnecting")}</p>
              )}
            </>
          ) : (
            <span className="qr-image" style={{ marginTop: "15px" }}>
              {t("sign.qr.update_part_1")} {" "}
              <a href="#" onClick={(e) => { e.preventDefault(); resetQR(); }}>
                {t("sign.qr.update_part_2")}
              </a>{" "}
              {t("sign.qr.update_part_3")}
            </span>
          )}
        </Container>
      )}
    </>
  );
};
